import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AdSlot from "@/components/AdSlot";
import { Search, Tv, Eye } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, SettingsContext } from "@/App";
import { cn } from "@/lib/utils";

// Shared homepage rendering components
import {
  DEFAULT_HERO,
  ChannelCard,
  HeroSliderSection,
  BannerSection,
  CategoryPillsSection,
  renderSection,
} from "@/components/HomepageRenderer";

// ============ MAIN HOMEPAGE ============

export default function Home() {
  const { settings: siteSettings } = useContext(SettingsContext);
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("fr") ? "fr" : "en";

  // CMS data
  const [cmsData, setCmsData] = useState(null);
  const [cmsLoading, setCmsLoading] = useState(true);
  const [cmsError, setCmsError] = useState(null);

  // All channels (for grid, filtering, search)
  const [allChannels, setAllChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);

  // Filters
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24;

  const isPremium = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user_data") || "{}");
      return u.has_membership && u.membership_expires && new Date(u.membership_expires) > new Date();
    } catch { return false; }
  }, []);

  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const urlSearch = searchParams.get("search");
    if (urlSearch) { setSearch(urlSearch); setCurrentPage(1); }
    const urlCountry = searchParams.get("country");
    if (urlCountry) { setSelectedCountry(urlCountry); setCurrentPage(1); }
    if (location.state?.category) { setSelectedCategory(location.state.category); setCurrentPage(1); }
    if (location.state?.country) { setSelectedCountry(location.state.country); setCurrentPage(1); }
  }, [location.state, searchParams]);

  const fetchData = useCallback((retries = 3) => {
    setCmsError(null);
    setCmsLoading(true);

    const loadCms = axios.get(`${API}/public/homepage`).catch(() => null);
    const loadChannels = axios.get(`${API}/channels`).catch(() => ({ data: [] }));
    const loadCategories = axios.get(`${API}/categories`).catch(() => ({ data: [] }));
    const loadCountries = axios.get(`${API}/countries`).catch(() => ({ data: [] }));

    Promise.all([loadCms, loadChannels, loadCategories, loadCountries])
      .then(([cmsRes, chRes, catRes, countryRes]) => {
        let cms = null;
        if (cmsRes?.data) {
          const body = cmsRes.data;
          cms = body.data || body;
        }

        if (cms) {
          setCmsData(cms);
          setCategories((cms.categories?.length ? cms.categories : null) || catRes.data || []);
          setCountries((cms.countries?.length ? cms.countries : null) || countryRes.data || []);
        } else {
          setCmsData(null);
          setCategories(Array.isArray(catRes.data) ? catRes.data : []);
          setCountries(Array.isArray(countryRes.data) ? countryRes.data : []);
        }
        setAllChannels(Array.isArray(chRes.data) ? chRes.data : []);
        setCmsLoading(false);
      })
      .catch(() => {
        if (retries > 0) setTimeout(() => fetchData(retries - 1), 2000);
        else { setCmsError("Failed to load homepage"); setCmsLoading(false); }
      });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Derived data
  const filteredChannels = allChannels.filter((ch) => {
    const matchesSearch = !search || (ch.name || "").toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory ? ch.category_id === selectedCategory : true;
    const matchesCountry = selectedCountry ? ch.country_id === selectedCountry : true;
    return matchesSearch && matchesCategory && matchesCountry;
  });
  const totalPages = Math.ceil(filteredChannels.length / itemsPerPage);
  const paginatedChannels = filteredChannels.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const isFiltering = search || selectedCategory || selectedCountry;

  // Error state with fallback hero
  if (cmsError && allChannels.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6" data-testid="home-error">
        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
          className="relative rounded-[24px] overflow-hidden mb-10 min-h-[280px] sm:min-h-[360px]">
          <img src={DEFAULT_HERO} alt="" role="presentation" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050814] via-[#050814]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050814] via-transparent to-transparent" />
          <div className="relative z-10 flex flex-col justify-end h-full min-h-[280px] sm:min-h-[360px] p-6 sm:p-10 lg:p-14">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3 leading-tight max-w-2xl" style={{ fontFamily: "Poppins" }}>
              {t("hero.title1")}<br /><span className="text-[#FFC928]">{t("hero.title2")}</span>
            </h1>
            <p className="text-[#94A3B8] text-sm sm:text-base max-w-lg mb-6">{t("hero.subtitle")}</p>
          </div>
        </motion.section>
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-red-400 text-center text-sm">{t("home.loadError")}</p>
          <button onClick={() => fetchData(3)} className="px-6 py-3 bg-[#FFC928] text-black rounded-xl font-bold text-sm hover:shadow-[0_0_20px_rgba(255,201,40,0.3)] transition-all" data-testid="retry-btn">
            {t("home.retry")}
          </button>
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (cmsLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8" data-testid="home-loading">
        <div className="h-64 sm:h-80 rounded-[24px] bg-[#0F172A] animate-pulse mb-10" />
        <div className="flex gap-2 mb-8 overflow-hidden">{[1,2,3,4,5].map(i => <div key={i} className="h-9 w-24 bg-[#0F172A] rounded-xl animate-pulse flex-shrink-0" />)}</div>
        <div className="mb-6"><div className="h-6 w-48 bg-[#0F172A] rounded animate-pulse mb-4" /></div>
        <div className="flex gap-4 mb-10 overflow-hidden">{[1,2,3,4].map(i => <div key={i} className="flex-shrink-0 w-[280px] h-56 bg-[#0F172A] rounded-[20px] animate-pulse border border-white/[0.06]" />)}</div>
        <div className="h-6 w-40 bg-[#0F172A] rounded animate-pulse mb-4" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {[1,2,3,4,5,6,7,8].map(i => (
            <div key={i} className="rounded-[18px] overflow-hidden bg-[#0F172A] border border-white/[0.06]">
              <div className="aspect-video bg-[#172033] animate-pulse" />
              <div className="p-3.5"><div className="h-4 w-3/4 bg-[#172033] rounded animate-pulse mb-2" /><div className="h-3 w-1/2 bg-[#172033] rounded animate-pulse" /></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Section data for renderers
  const sectionData = {
    sliders: cmsData?.sliders ?? [],
    banners: cmsData?.banners ?? [],
    featured_channels: cmsData?.featured_channels?.length
      ? cmsData.featured_channels
      : allChannels.filter(c => c.is_featured === 1).sort((a, b) => (a.featured_order || 999) - (b.featured_order || 999)),
    trending_channels: cmsData?.trending_channels?.length
      ? cmsData.trending_channels
      : [...allChannels].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 15),
    recently_added: cmsData?.recently_added ?? [],
    categories,
    countries,
    allChannels,
    settings: siteSettings,
    isPremium,
    onSelectCategory: (id) => { setSelectedCategory(id); setCurrentPage(1); },
    selectedCategory,
  };

  const sections = cmsData?.sections ?? [];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6" data-testid="home-page">

      {/* Ad: homepage top announcement */}
      {!isFiltering && currentPage === 1 && <AdSlot placement="homepage_top" className="mb-6" />}

      {/* CMS-DRIVEN SECTIONS — only on first page, not filtering */}
      {!isFiltering && currentPage === 1 && (
        <>
          {sections.length > 0
            ? sections
                .filter(s => s.is_visible !== false)
                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                .map(section => renderSection(section, sectionData, locale, t))
            : (
              <>
                <HeroSliderSection sliders={[]} locale={locale} channels={allChannels} settings={siteSettings} isPremium={isPremium} t={t} />
                <CategoryPillsSection categories={categories} locale={locale} t={t} onSelect={(id) => { setSelectedCategory(id); setCurrentPage(1); }} selected={selectedCategory} />
              </>
            )
          }

          {/* Homepage banners by placement */}
          <BannerSection banners={sectionData.banners} placement="homepage_top" locale={locale} />
          <BannerSection banners={sectionData.banners} placement="homepage_middle" locale={locale} />
          <BannerSection banners={sectionData.banners} placement="homepage_bottom" locale={locale} />
        </>
      )}

      {/* SEARCH & FILTERS (when actively filtering) */}
      {(isFiltering || currentPage > 1) && (
        <section className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-grow">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={18} />
              <input type="text" placeholder={t("home.searchChannels")} data-testid="search-input-inline"
                className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white/[0.05] border border-white/[0.08] text-white placeholder:text-[#94A3B8]/60 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20 text-sm"
                value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} />
            </div>
            <select data-testid="country-filter"
              className="px-4 py-3 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white focus:outline-none text-sm appearance-none cursor-pointer"
              value={selectedCountry || ""} onChange={(e) => { setSelectedCountry(e.target.value || null); setCurrentPage(1); }}>
              <option value="">{t("home.allCountries")}</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {(search || selectedCategory || selectedCountry) && (
            <button onClick={() => { setSearch(""); setSelectedCategory(null); setSelectedCountry(null); setCurrentPage(1); }}
              className="text-xs text-[#FFC928] hover:underline" data-testid="clear-filters-btn">{t("home.clearFilters")}</button>
          )}
        </section>
      )}

      {/* ALL CHANNELS HEADING */}
      <section className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: "Poppins" }}>
            {isFiltering ? t("home.results", { count: filteredChannels.length }) : t("home.allChannels")}
          </h2>
          {!isFiltering && (
            <select data-testid="country-filter-inline"
              className="px-3 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-[#94A3B8] text-xs focus:outline-none cursor-pointer"
              value={selectedCountry || ""} onChange={(e) => { setSelectedCountry(e.target.value || null); setCurrentPage(1); }}>
              <option value="">{t("home.allCountries")}</option>
              {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
      </section>

      {/* CHANNEL GRID */}
      <section>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-testid="channel-grid">
          {paginatedChannels.map((ch, idx) => <ChannelCard key={ch.id} channel={ch} idx={idx} />)}
        </div>

        {filteredChannels.length === 0 && (
          <div className="text-center py-24 rounded-[24px] bg-[#0F172A] border border-dashed border-white/[0.08]" data-testid="no-channels">
            <Tv className="mx-auto text-[#94A3B8]/30 mb-4" size={48} />
            <h3 className="text-lg font-medium text-white">{t("home.noChannelsTitle")}</h3>
            <p className="text-[#94A3B8] text-sm mt-1">{t("home.noChannelsSubtitle")}</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-8 flex justify-center" data-testid="pagination">
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}
                className="px-4 py-2 rounded-xl bg-white/[0.05] border border-white/[0.08] text-[#94A3B8] hover:text-white disabled:opacity-30 transition-colors text-sm" data-testid="pagination-prev">{t("home.previous")}</button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) page = i + 1;
                else if (currentPage <= 3) page = i + 1;
                else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                else page = currentPage - 2 + i;
                return (
                  <button key={page} onClick={() => setCurrentPage(page)}
                    className={cn("w-10 h-10 rounded-xl flex items-center justify-center text-sm font-medium transition-all",
                      currentPage === page ? "bg-[#FFC928] text-black shadow-[0_0_15px_rgba(255,201,40,0.2)]" : "bg-white/[0.05] border border-white/[0.08] text-[#94A3B8] hover:text-white")}
                    data-testid={`pagination-page-${page}`}>
                    {page}
                  </button>
                );
              })}
              <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-xl bg-white/[0.05] border border-white/[0.08] text-[#94A3B8] hover:text-white disabled:opacity-30 transition-colors text-sm" data-testid="pagination-next">{t("home.next")}</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
